<script setup>
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  suggestion: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'keydown', 'acceptSuggestion'])

const { t } = useI18n()
const inputReference = ref(null)

function onClear() {
  emit('update:modelValue', '')
}

function onEscape() {
  emit('update:modelValue', '')
  inputReference.value?.$el?.querySelector('input')?.blur()
}

function onInputKeydown(event) {
  if (event.key === 'Escape') {
    onEscape()
    return
  }

  if (props.suggestion) {
    if (event.key === 'Tab') {
      event.preventDefault()
      emit('acceptSuggestion', props.suggestion)
      return
    }
    if (event.key === 'ArrowRight') {
      const input = inputReference.value?.$el?.querySelector('input')
      if (input && input.selectionStart === input.value.length) {
        event.preventDefault()
        emit('acceptSuggestion', props.suggestion)
        return
      }
    }
  }

  if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Enter') {
    event.preventDefault()
    emit('keydown', event)
  }
}

function focus() {
  inputReference.value?.$el?.querySelector('input')?.focus()
}

defineExpose({ focus })
</script>

<template>
  <div class="flex flex-col gap-1">
    <div class="relative flex items-center">
      <dss-input-text
        ref="inputReference"
        :model-value="props.modelValue"
        :placeholder="t('menu.search')"
        icon="pi-search"
        size="small"
        class="w-full"
        @update:model-value="value => emit('update:modelValue', value)"
        @keydown="onInputKeydown"
      />

      <transition
        enter-active-class="transition-opacity duration-150"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <button
          v-if="props.modelValue"
          class="absolute right-2 flex items-center justify-center text-dss-surface-400 hover:text-dss-surface-700 transition-colors"
          type="button"
          tabindex="-1"
          @click="onClear"
        >
          <i class="pi pi-times text-xs" />
        </button>
      </transition>
    </div>

    <transition
      enter-active-class="transition-opacity duration-150"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <button
        v-if="suggestion"
        type="button"
        class="flex items-center gap-1.5 px-1 text-left truncate"
        tabindex="-1"
        @click="emit('acceptSuggestion', suggestion)"
      >
        <span class="text-xs font-hellix text-dss-surface-600 truncate">{{ suggestion }}</span>
        <kbd class="shrink-0 text-[10px] font-hellix text-dss-surface-400 bg-dss-surface-100 border border-dss-surface-200 rounded px-1">Tab</kbd>
      </button>
    </transition>
  </div>
</template>
